<i18n src="./resources/locales.json"></i18n>
<i18n src="@/mixins/ecs-time-formatter/resources/locales.json"></i18n>

<template>
  <EcsNotificationsPanel
    ref="notifications_panel"
    :open="open"
    :panel-name="$t('notifications')"
    :items="items"
    @onItemClick="onItemClick"
  >
    <template #fab>
      <v-fab-transition>
        <v-btn v-if="showDismissAll" color="primary" fab x-small @click="dismissAllNotifications">
          <v-tooltip color="surface lighten-1" left>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-notification-clear-all
              </v-icon>
            </template>
            <span v-once>
              {{ $t('dismiss_all') }}
            </span>
          </v-tooltip>
        </v-btn>
      </v-fab-transition>
    </template>
  </EcsNotificationsPanel>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import {
  STATE_MISSED,
  MEDIA_TYPE_TELEPHONY,
  MEDIA_TYPE_CHAT,
  MEDIA_TYPE_EMAIL,
  MEDIA_TYPE_SMS
} from '@/constants/socket-events';
import EcsNotificationsPanel from '@/components/ecs-notifications-panel/EcsNotificationsPanel';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

export default {
  name: 'EcsAgentNotifications',

  components: {
    EcsNotificationsPanel
  },

  mixins: [EcsTimeFormatter],

  data() {
    return {
      open: false
    };
  },

  computed: {
    ...mapGetters('CONTACTS', ['getAgentByEndPoint']),

    ...mapGetters('NOTIFICATIONS', ['getSystemNotifications']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    showDismissAll() {
      return this.items && this.items.length > 2;
    },
    items() {
      return this.getSystemNotifications || [];
    },
    language() {
      return this.applicationSettings.language;
    }
  },
  watch: {
    open(newValue) {
      if (newValue) {
        this.updateNotificationsTimeAgo([...this.getSystemNotifications]);
      }
    },
    language() {
      this.updateNotificationsTimeAgo([...this.getSystemNotifications]);
    }
  },
  sockets: {
    INTERACTION_STATE_CHANGED_EVENT(interaction) {
      const { interactionStateName } = interaction;

      if (interactionStateName.toUpperCase() === STATE_MISSED) {
        const ecsInteraction = new EcsInteraction(interaction);
        const missedInteraction = this.createdMissedInteractionNotification(ecsInteraction);
        this.addSystemNotification(missedInteraction);
      }
    }
  },
  mounted() {
    this.$root.$on('add-welcome-notification', this.addWelcomeNotification);
    this.$root.$on('create-missed-interaction-notification', this.createMissedInteractionNotification);
  },
  beforeDestroy() {
    this.$root.$off('add-welcome-notification', this.addWelcomeNotification);
    this.$root.$off('create-missed-interaction-notification', this.createMissedInteractionNotification);
  },
  methods: {
    ...mapActions('NOTIFICATIONS', [
      'addSystemNotification',
      'dismissAllSystemNotifications',
      'dismissSystemNotification',
      'updateSystemNotifications'
    ]),

    setOpen(value) {
      this.open = value;
    },

    isOpened() {
      return this.open;
    },

    createMissedInteractionNotification(interaction) {
      const missedInteraction = this.createdMissedInteractionNotification(interaction);
      this.addSystemNotification(missedInteraction);
    },

    /**
     * Creates an EcsNotificationItem for a missed interaction (Chat/Telephony/Email)
     * @param interaction
     * @returns {object}
     */
    createdMissedInteractionNotification(interaction) {
      const notification = {
        _type: 'MISSED_INTERACTION_NOTIFICATION',
        id: interaction.interactionId,
        iconColor: '#C30B00',
        interaction: { ...interaction }
      };

      const notificationContent = this.getMissedInteractionNotificationContent(notification);

      return Object.assign(notification, notificationContent);
    },

    /**
     * Creates the message body and header for a missed interaction notification
     * @param notification
     * @returns {object}
     */
    getMissedInteractionNotificationContent(notification) {
      const content = {
        icon: '',
        notificationType: '',
        time: Date.now(),
        timeAgo: '',
        headline: '',
        message: '',
        messageVars: {},
        actions: []
      };
      const consultationAgentNames = this.getConsultationAgentNames(notification.interaction);
      const agent = this.getAgentByEndPoint(notification.interaction.origin) || {};
      const messageVars = this.getMessageVars(notification, consultationAgentNames, agent);

      const { icon, headline, message } = this.setMissedInteractionNotificationProperties(notification);

      content.icon = icon;
      content.notificationType = this.setNotificationType(notification);
      content.timeAgo = this.fromNow(content.time, 'UTC');
      content.headline = { i18nKey: headline, date: '' };
      content.message = message;
      content.messageVars = messageVars;
      content.timestamp = moment().unix();
      content.actions = [
        {
          name: 'notification.actions.dismiss',
          action: 'dismissNotification'
        }
      ];

      return content;
    },

    setMissedInteractionNotificationProperties({ interaction }) {
      const content = {
        icon: 'mdi-help-circle',
        headline: 'notification.type.notification',
        message: 'notification.body.general'
      };
      const mediaType = interaction.interactionMediaType.toUpperCase();
      switch (mediaType) {
        case MEDIA_TYPE_CHAT:
          content.icon = 'mdi-message-alert';
          content.headline = 'notification.headline.missedChat';
          if (interaction.isInternal || this.isConsultationInteraction(interaction)) {
            // TODO: We can add avatar here via agent.avatar
            content.message = 'notification.body.internalChat';
          } else {
            content.message = 'notification.body.externalChat';
          }
          break;
        case MEDIA_TYPE_TELEPHONY:
          content.icon = 'mdi-phone-missed';
          content.headline = 'notification.headline.missedCall';
          if (interaction.isInternal || this.isConsultationInteraction(interaction)) {
            // TODO: We can add avatar here via agent.avatar
            content.message = 'notification.body.internalCall';
          } else {
            content.message = 'notification.body.externalCall';
          }
          break;
        case MEDIA_TYPE_EMAIL:
          content.icon = 'mdi-email';
          content.headline = 'notification.headline.missedEmail';
          content.message = 'notification.body.missedEmail';
          break;
        case MEDIA_TYPE_SMS:
          content.icon = 'mdi-message-alert';
          content.headline = 'notification.headline.missedSms';
          content.message = 'notification.body.missedSms';
          break;
        default:
          break;
      }

      return content;
    },

    /**
     * Return the proper translation for notification type
     * @param {object} notification
     * @returns {string}
     */
    setNotificationType(notification) {
      let notificationType = 'notification.type.missedInteraction';
      if (this.isConsultationInteraction(notification.interaction)) {
        notificationType = 'notification.type.missedConsultation';
      }

      return notificationType;
    },

    getMessageVars(notification, consultationAgentNames, agent) {
      const interaction = new EcsInteraction(notification.interaction);
      return {
        agentName: consultationAgentNames || agent.name,
        bpName: interaction.routing.businessProcessName,
        customerName: interaction.formattedCustomerFullName || interaction.receptor.name || this.$t('unknown'),
        origin:
          (interaction.isChat && interaction.isCustomerHandling) || interaction.isEmail
            ? interaction.receptor.email
            : interaction.receptor.endPoint,
        time: moment(interaction.timestamp).format(this.applicationSettings.timeFormat)
      };
    },

    /**
     * Verifies if an interaction is a consult based on the connected agent list that are involved in the interaction.
     * @param interaction
     */
    isConsultationInteraction({ connectedPartiesListNew }) {
      return (connectedPartiesListNew || []).filter(contact => contact.partyType !== 'Customer').length > 1;
    },

    /**
     * Dismisses a single notification (on-click)
     * @param notification
     */
    dismissNotification(notification) {
      this.dismissSystemNotification(notification);
    },

    /**
     * Updates the notifications counter to a specific number
     * @param count
     */
    updateNotificationsCounter(count) {
      this.$emit('notifications-counter-update', count);
    },

    /**
     * Dismisses all notifications by clearing the items array
     */
    dismissAllNotifications() {
      this.dismissAllSystemNotifications();
    },

    /**
     * Returns all agent names that are involved in the consultation separated by comma.
     * @param interaction
     */
    getConsultationAgentNames(interaction) {
      if (!this.isConsultationInteraction(interaction)) {
        return null;
      }

      return interaction.connectedPartiesListNew
        .filter(contact => contact.partyType !== 'Customer')
        .map(agent => {
          return agent.name;
        })
        .join(', ');
    },
    onItemClick(item, action) {
      this[action](item);
    },
    updateNotificationsTimeAgo(items) {
      const updatedItems = items.map(item => {
        item.timeAgo = moment(item.time).fromNow();
        return item;
      });
      this.updateSystemNotifications(updatedItems);
    }
  }
};
</script>

<style></style>
